// Story 7.2 (ADO-905) — Catalogue keyword-search read client.
//
// Thin typed wrapper over the Bearer-attaching `apiFetch` transport for the
// SearchAndDiscovery partial-text/fuzzy search read path (FR12). Mirrors
// `browse.client.ts`: it is the SINGLE consumer seam until the
// `openapi-typescript`-generated client lands, and it is exercised against msw
// in tests. The backend serves this route under the `/api/v1` global prefix
// (ADO-1181), matching the `API_PREFIX` path this client builds exactly as the
// browse client does.
//
// SCOPE NOTE: the result rows reuse `ProductSummary` (name / description / pack
// size / unit of measure from Story 7.1, plus the Brand Bank image URL from
// Story 7.4) — price (Epic 8), stock (7.3) and favourites (7.5) remain absent
// seams. Add-to-basket from a result is Epic 9 / FR103 (AC7): the surface may
// show an "Add" affordance but performs no basket write here.

import { API_PREFIX, apiFetch } from './shoplink-api';
import type { OfferKind, ProductSummary } from './browse.client';
import { ENTRY_PARAM, appendCustomerAccountParam } from './catalogue-entry';
import {
  appendFilterParams,
  type CatalogueNarrowing,
  type Facet,
  type ProductFilters,
} from './facets';

/**
 * Story 8.2 (ADO-2011), AC6 — one search result row. A live product is a plain
 * `ProductSummary`. A DISCONTINUED product an exact code / EAN / TUC search surfaced
 * (because it has a live substitute at the Shopper's depot) carries
 * `notAvailable: true`: it renders as "Product not currently available on shoplink."
 * with "See the substitute" and no Add. Lock-step with `SearchResultProductDto`.
 */
export type SearchResultProduct = ProductSummary & { readonly notAvailable?: true };

/** The result of a keyword search — matching products, the echoed query, and a
 *  zero-results flag so the UI can render an explicit empty state (AC3). */
export interface ProductSearchResult {
  /** The (server-sanitised, trimmed) term that was searched — echoed for AC3. */
  readonly query: string;
  /** Matching products, ranked by relevance (AC1). Empty for zero results (AC3).
   *  Story 8.2 (ADO-2011): may include `notAvailable` exact hits. */
  readonly products: readonly SearchResultProduct[];
  /** True when nothing matched — the UI renders the empty state, not an error. */
  readonly isEmpty: boolean;
  /** Story 7-FU-C (ADO-1216), AC3/AC6: `true` when the result set was capped at
   *  the server's search limit (there are more matches than shown) — the UI shows
   *  a truncation notice instead of presenting the capped set as complete. `false`
   *  for a zero-results or blank-query response. Lock-step with `ProductSearchResultDto`. */
  readonly truncated: boolean;
  /** Story 7.10 (ADO-906): available attribute facets for the result set (AC5),
   *  bounded by the search result cap. Lock-step with `ProductSearchResultDto`. */
  readonly facets: readonly Facet[];
  /** Story 7.29 (ADO-2025): the catalogue narrowings this result offers, as on a listing.
   *  Lock-step with `ProductSearchResultDto.narrowings`; absent offers no narrowing. */
  readonly narrowings?: readonly CatalogueNarrowing[];
  /** Story 7.29 (ADO-2025, D-7.29-25): the offered narrowings with nothing to narrow, shown
   *  disabled. Lock-step with `ProductSearchResultDto.unavailableNarrowings`. */
  readonly unavailableNarrowings?: readonly CatalogueNarrowing[];
  /** Story 7.29 (ADO-2025, D-7.29-29): the facet selection the search applied. Lock-step with
   *  `ProductSearchResultDto.appliedFilters`. */
  readonly appliedFilters?: ProductFilters;
  /** Story 7.32 (ADO-1115): the offer kinds a search within the collected offers could not load
   *  this time (today only `FEATURED_LINE`). Lock-step with
   *  `ProductSearchResultDto.unavailableOfferKinds`; absent when none. */
  readonly unavailableOfferKinds?: readonly OfferKind[];
}

export const SEARCH_PATH = `${API_PREFIX}/catalogue/search`;

/** Server-side Zod cap on `q` (search-query.validation.ts, lock-step) — the
 *  input enforces it so a long paste can't produce an un-retryable 400. */
export const MAX_SEARCH_QUERY_LENGTH = 128;

/**
 * Run a partial-text/fuzzy product search for `q` (AC1/AC2). A zero-results
 * response is a valid `{ products: [], isEmpty: true }` (AC3), never an error;
 * only a transport/non-2xx failure throws so the UI can surface it separately.
 *
 * Story 7-FU-D (ADO-1217), AC2: an optional `AbortSignal` lets a caller abort a
 * superseded/unmounted search — it threads straight through `apiFetch`'s `init`.
 * The arg is optional + last so existing callers (and their tests) are unchanged;
 * an aborted fetch rejects with a DOMException named `AbortError`, which the
 * caller distinguishes from a genuine transport failure.
 *
 * Enabler E7-C (ADO-2274), D-1: `entry` is the selected ordering entry. The server
 * searches that entry's range only, and shows a discontinued exact hit (Story 8.2,
 * ADO-2011, AC6) only when it has a live substitute at the entry's depot. Story 7.29 (ADO-2025),
 * D-7.29-21: `customerAccountCode` is the active Trading Account; `null` names none.
 *
 * Story 7.31 (ADO-1113), AC1 (PR #862 review): `collectedOffers` searches within the collected
 * offers (`offers=all`), the union the listing's collected-offers mode lists, so a term committed
 * inside "Everything on offer" searches the collection rather than the whole range.
 */
export async function searchProducts(
  entry: string,
  q: string,
  filters: ProductFilters = {},
  signal?: AbortSignal,
  customerAccountCode: string | null = null,
  collectedOffers = false,
): Promise<ProductSearchResult> {
  const params = new URLSearchParams({ q, [ENTRY_PARAM]: entry });
  appendCustomerAccountParam(params, customerAccountCode);
  appendFilterParams(params, filters);
  if (collectedOffers) params.set('offers', 'all');
  return fetchSearch(params, signal);
}

/**
 * Bug 3731: the typeahead's suggestions for `q`, listed by product code ascending, as SL1.0's
 * typeahead lists them, rather than by the results page's relevance ranking. The server's
 * matching, range and exact-hit-first rules are the same as {@link searchProducts}; only the
 * order differs (`sort=productCode`, lock-step with the API's `SEARCH_SORT_OPTIONS`). The
 * optional `signal` aborts a superseded request, as in {@link searchProducts}.
 */
export async function searchSuggestions(
  entry: string,
  q: string,
  signal?: AbortSignal,
): Promise<ProductSearchResult> {
  return fetchSearch(new URLSearchParams({ q, [ENTRY_PARAM]: entry, sort: 'productCode' }), signal);
}

async function fetchSearch(
  params: URLSearchParams,
  signal: AbortSignal | undefined,
): Promise<ProductSearchResult> {
  const res = await apiFetch(`${SEARCH_PATH}?${params.toString()}`, signal ? { signal } : {});
  if (!res.ok) throw new Error(`Failed to search products (${res.status})`);
  return (await res.json()) as ProductSearchResult;
}
